import { reactive } from 'vue';
import { formatPhoneNumber, formatPostalCode } from '@/utils/dataUtils';
import useVuelidate from '@vuelidate/core';
import { usePageHelper } from '@/composables/pageHelper';
import { computed } from 'vue';

/**
 * A composable helper for managing form state and operations.
 * @param {Object} [initialData={}] - The initial data for the form.
 * @param {Object} [rules={}] - Validation rules for the form.
 * @returns {Object} - The form helper methods and state.
 */
export function useFormHelper(initialData = {}, rules = {}) {
  const formData = reactive(initialData);
  const pageHelper = usePageHelper(); // Use the entire pageHelper object
  const { showLoading, save, fetch, showToast, setFocus, focusFirstInvalidInput, setErrorMessage, focusFirstEnabledInput, focusErrorMessage, scrollIntoView, scrollToTop } = pageHelper;

  const state = reactive({
    data: formData,
    setErrorMessage: setErrorMessage,
    isLoading: computed(() => pageHelper.state.isLoading),
    isProcessing: computed(() => pageHelper.state.isProcessing),
    message: computed(() => pageHelper.state.message),
    isSaving: computed(() => pageHelper.state.isSaving),
    hasError: computed(() => pageHelper.state.hasError),
    errorMessage: computed(() => pageHelper.state.errorMessage),
    $v: useVuelidate(rules, formData, { $lazy: true, $autoDirty: true })
  });


  /**
   * Updates the form data with new values while maintaining reactivity.
   * @param {Object} newData - The new data to merge into the form state.
   */
  function setData(newData) {
    Object.assign(state.data, newData);
  }

  /**
   * Resets the form data to initial values or provided values.
   * @param {Object} [resetData=initialData] - The data to reset to.
   */
  function resetData(resetData = initialData) {
    Object.assign(state.data, resetData);
    state.$v.$reset();
  }
  /**
   * Sanitizes a number input by removing non-digit characters.
   * @param {string} $event - The input event value.
   * @returns {string} - The sanitized number containing only digits.
   */
  function sanitizeNumber($event) {
    return $event.replace(/\D/g, '');
  }

  /**
   * Formats a phone number.
   * @param {string} value - The phone number to format.
   * @returns {string} - The formatted phone number.
   */
  function getFormattedPhoneNumber(value) {
    return formatPhoneNumber(value);
  }

  /**
   * Formats a postal code.
   * @param {string} value - The postal code to format.
   * @returns {string} - The formatted postal code.
   */
  function getFormattedPostalCode(value) {
    return formatPostalCode(value);
  }

  /**
   * Determines if the form is savable based on its dirty state and validation errors.
   * @returns {boolean} - True if the form is savable, false otherwise.
   */
  const isSavable = () => {
    // If the data isn't dirty then it's not savable
    if (typeof state.data.isDirty === 'function' && !state.data.isDirty()) {
      return false;
    }

    // If the data is dirty, check if there are any validation errors   
    if (state.$v.$anyDirty) {
      state.$v.$touch();
      return !state.$v.$error;
    } else {
      return state.$v.$error;
    }
  };

  return {
    state,

    save,
    fetch,
    focusFirstInvalidInput,
    focusErrorMessage,
    focusFirstEnabledInput,
    scrollIntoView,
    scrollToTop,
    showToast,
    setFocus,
    showLoading,
    sanitizeNumber,
    getFormattedPostalCode,
    getFormattedPhoneNumber,
    isSavable,
    setData,
    resetData
  };
}